import { pictogramsData } from '@app/components/metric-table-value-build';
import { OptionItem } from '@app/components/ui/select/select.type';
import useTranslation from '@app/hooks/use-translation';
import { SingleScaleTemplateObjectAndCreateType } from '@app/interfaces/qm-module/qm-score-templates/qm-score-templates.type';
import {
  useCreateScoreTemplateMutation,
  useEditScoreTemplateMutation,
} from '@app/store/api/qm-module/qm-score-templates/qm-score-templates.api';
import {
  formBaseButtonBlockStyles,
  formFieldBaseStylesModal650,
  formFieldNameBaseStyles,
} from '@app/styles/base-styles';
import { Button, CircleButton, ColorInput, Input, Select } from '@ui';
import classNames from 'classnames';
import React, { FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

const labelTags = ['type', 'condition', 'value', 'description', 'color', 'icon'];

type FormType = SingleScaleTemplateObjectAndCreateType['rating_scale'];

type TemplateSettingFormPropsType = {
  templateValues: (SingleScaleTemplateObjectAndCreateType & { rs_template_id: string }) | undefined;
  onClose(): void;
};

const TemplateSettingForm: FC<TemplateSettingFormPropsType> = (props) => {
  const { onClose, templateValues } = props;
  const { t } = useTranslation('pages.qmScoreTemplates');
  const { t: tForm } = useTranslation('common.forms');
  const [createTemplate] = useCreateScoreTemplateMutation();
  const [editTemplate] = useEditScoreTemplateMutation();
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormType>({ defaultValues: templateValues?.rating_scale || {} });

  const { append, fields, remove } = useFieldArray({ name: 'items', control });

  const rating_scale_type_options: OptionItem[] = [
    { value: 'eq', title: t('criteria_eq') },
    { value: '>', title: '>' },
    { value: '<', title: '<' },
    { value: '>=', title: '>=' },
    { value: '<=', title: '<=' },
    { value: '=', title: '=' },
    { value: 'range', title: t('criteria_range') },
  ];
  const defaultForAutoRatingScale: FormType['items'][0] = {
    rating: 0,
    desc: '',
    icon: undefined,
    color: '#ffffff',
    type: 'eq',
    id: fields.length + 1,
    value: [],
  };
  const iconsOptionList = Object.entries(pictogramsData).map(([iconIndex]) => {
    return { value: iconIndex, title: iconIndex };
  });

  function handleAppendRatingScaleNewItem() {
    append(defaultForAutoRatingScale);
  }

  function handleRemoveRatingScaleEditItem(index: number) {
    remove(index);
  }

  function onSubmit(data: FormType) {
    const formattedData: SingleScaleTemplateObjectAndCreateType = {
      rating_scale: {
        ...data,
        items: (data.items || []).map((item, index) => ({
          ...item,
          id: index + 1,
          value: item.value,
        })),
      },
    };
    try {
      templateValues
        ? editTemplate({
            body: formattedData,
            params: { rs_template_id: templateValues.rs_template_id },
          })
        : createTemplate(formattedData);
    } finally {
      onClose();
    }
  }

  return (
    <form className="pb-4 flex flex-col gap-[20px] min-h-[450px]" onSubmit={handleSubmit(onSubmit)}>
      <div className={formFieldBaseStylesModal650}>
        <span className={formFieldNameBaseStyles}>{t('scale_name')}</span>
        <Input
          {...register('name', {
            required: { value: true, message: tForm('required') },
          })}
          error={errors?.['name']?.message}
        />
      </div>
      {!!fields.length && (
        <div
          className="grid grid-rows-1 gap-x-[20px] pt-[20px]
      grid-cols-[minmax(0,140px)_minmax(0,200px)_minmax(0,100px)_minmax(0,230px)_minmax(0,50px)_minmax(0,70px)_1fr]"
        >
          {labelTags.map((labelKey, index) => (
            <div className="text-center" key={index}>
              {t(`rating_scale_${labelKey}`)}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-[20px] pt-[20px] flex-1">
        {fields.map((item, index) => (
          <div key={item.id} className="flex items-center gap-[20px]">
            <div className="w-full max-w-[140px]">
              <Select
                options={rating_scale_type_options}
                // options={
                //   watch('settings.metric_id')
                //     ? metricValueType === 'str'
                //       ? rating_scale_type_options.slice(0, 1)
                //       : rating_scale_type_options.slice(1)
                //     : rating_scale_type_options
                // }
                defaultValue={watch(`items.${index}.type`)}
                onChange={(value) => {
                  setValue(`items.${index}.type`, value as FormType['items'][0]['type']);
                }}
              />
            </div>
            <div className="w-full flex items-center gap-[10px] max-w-[200px]">
              <Input
                {...register(
                  `items.${index}.value${watch(`items.${index}.type`) === 'range' ? '.0' : ''}`,
                )}
                type={watch(`items.${index}.type`) === 'range' ? 'number' : 'text'}
                // type={metricValueType && metricValueType === 'num' ? 'number' : 'text'}
              />
              {watch(`items.${index}.type`) === 'range' && (
                <Input
                  {...register(`items.${index}.value.1`)}
                  type={watch(`items.${index}.type`) === 'range' ? 'number' : 'text'}
                  // type={metricValueType && metricValueType === 'num' ? 'number' : 'text'}
                />
              )}
            </div>

            <div className="w-full max-w-[100px]">
              <Input
                {...register(`items.${index}.rating`, {
                  // valueAsNumber: true,
                  max: { value: 100, message: '<=100%' },
                })}
                placeholder={t('rating_scale_rating')}
                step={0.1}
                type="number"
                error={errors?.items?.[index]?.rating?.message}
              />
            </div>

            <Input
              {...register(`items.${index}.desc`, {
                required: { value: true, message: tForm('required') },
              })}
              placeholder={t('rating_scale_description')}
              error={errors?.['items']?.[index]?.['desc']?.message}
            />
            {/* </div> */}
            <ColorInput
              color={watch(`items.${index}.color`)}
              changeColor={(color) => setValue(`items.${index}.color`, color)}
            />
            <div className="w-full max-w-[100px]">
              <Select
                defaultValue={String(watch(`items.${index}.icon`)) || undefined}
                onChange={(value) => setValue(`items.${index}.icon`, Number(value))}
                options={iconsOptionList}
                // error={errors.settings?.select?.[index]?.icon?.message}
                pictogramsMode
                position="right"
                pictogramListSize={28}
                dropWidth={320}
              />
            </div>

            <div>
              <CircleButton
                icon={'TrashIcon'}
                size={14}
                className="text-basic_red"
                onClick={() => handleRemoveRatingScaleEditItem(index)}
              />
            </div>
          </div>
        ))}
        <div className="flex w-full items-center justify-end">
          <Button
            onClick={handleAppendRatingScaleNewItem}
            fill="linked"
            label={!fields.length ? t('add_scale') : ''}
            icon="PlusIcon"
            sizeIcon={14}
          />
        </div>
      </div>

      <div className={classNames(formBaseButtonBlockStyles)}>
        <Button type="submit" label={t('approve_button')} />
        <Button fill="linked" onClick={onClose} label={t('cancel_button')} />
      </div>
    </form>
  );
};

export default TemplateSettingForm;
